import { ArrowLeft, Check, Printer, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { renderMarkdown } from "@navibiz/chat-core/markdown";
import { api } from "../../lib/api";
import { PRODUCT_LABEL, when } from "../../lib/format";
import { keys, useAction, useTestRun } from "../../lib/queries";
import type { TestResult } from "../../lib/types";
import { Button } from "../../ui/Button";
import { ErrorState, PageSkeleton, useToast } from "../../ui/Feedback";
import styles from "./Document.module.css";

/**
 * Berita Acara Uji Jawaban — the acceptance record from step 6 of the AI
 * implementation flow. Judged on screen with the client's PIC, then printed
 * or saved as PDF from the browser for signatures.
 */
export function TestRunPage() {
  const { projectId = "", runId = "" } = useParams();
  const run = useTestRun(projectId, runId);
  const toast = useToast();
  const [results, setResults] = useState<TestResult[]>([]);
  useEffect(() => {
    setResults(run.data?.results ?? []);
  }, [run.data]);

  const save = useAction(
    (body: { results: Pick<TestResult, "caseId" | "verdict" | "note">[] }) => api.patch<{ passed: number; total: number; status: string }>(`/projects/${projectId}/test-runs/${runId}`, body),
    [keys.testRun(projectId, runId), keys.tests(projectId)],
  );

  if (run.isPending) return <PageSkeleton />;
  if (run.isError) return <ErrorState error={run.error} />;
  const data = run.data;
  const judged = results.filter((r) => r.verdict).length;
  const passed = results.filter((r) => r.verdict === "pass").length;
  const set = (caseId: string, patch: Partial<TestResult>) => setResults((current) => current.map((r) => (r.caseId === caseId ? { ...r, ...patch } : r)));

  return (
    <div className={styles.page}>
      <div className={styles.toolbar} data-print-hide>
        <Link to={`/projects/${projectId}/tests`}>
          <Button variant="ghost" icon={<ArrowLeft aria-hidden />}>
            Kembali ke uji terima
          </Button>
        </Link>
        <div className={styles.toolbarRight}>
          <span className={styles.progress}>
            {judged}/{results.length} dinilai · {passed} lolos
          </span>
          <Button
            loading={save.isPending}
            onClick={async () => {
              const result = await save.mutateAsync({ results: results.map(({ caseId, verdict, note }) => ({ caseId, verdict, note })) });
              toast(result.status === "reviewed" ? `Berita acara final: ${result.passed}/${result.total} lolos.` : "Penilaian disimpan.");
            }}
          >
            Simpan penilaian
          </Button>
          <Button variant="primary" icon={<Printer aria-hidden />} onClick={() => window.print()}>
            Cetak / PDF
          </Button>
        </div>
      </div>

      <article className={styles.document}>
        <header className={styles.docHeader}>
          <div>
            <div className={styles.docKicker}>PT Cetta Adika Nava · Navibiz</div>
            <h1>Berita Acara Uji Jawaban</h1>
            <p>
              Putaran {data.round} · {data.knowledgeVersion ? `Pengetahuan versi v${data.knowledgeVersion}` : "Pengetahuan draf"}
            </p>
          </div>
          <dl className={styles.docMeta}>
            <dt>Klien</dt>
            <dd>{data.project.clientName}</dd>
            <dt>Chatbot</dt>
            <dd>
              {data.project.name} · {PRODUCT_LABEL[data.project.product]}
            </dd>
            <dt>Tanggal uji</dt>
            <dd>{when.dateTime(data.createdAt)}</dd>
            <dt>Hasil</dt>
            <dd>
              <strong>
                {passed} dari {results.length} lolos
              </strong>
            </dd>
          </dl>
        </header>

        <p className={styles.docIntro}>
          Pertanyaan berikut dijalankan pada chatbot sebagai percakapan baru. Kategori <em>jebakan</em> menguji topik di luar usaha, harga yang tidak ada, dan upaya
          membobol instruksi. Untuk produk dengan Linkage, rujukan yang muncul di bawah jawaban ikut dinilai. Jawaban yang dinilai “tidak lolos” diperbaiki dalam
          putaran penyesuaian berikutnya.
        </p>

        <ol className={styles.results}>
          {results.map((result, i) => (
            <li key={result.caseId} className={styles.result}>
              <div className={styles.resultHead}>
                <span className={styles.resultNo}>{i + 1}</span>
                <div className={styles.resultQ}>
                  {result.category === "jebakan" && <span className={styles.trap}>Jebakan</span>}
                  {result.question}
                </div>
                <div className={styles.verdict} role="group" aria-label={`Penilaian pertanyaan ${i + 1}`}>
                  <button type="button" aria-pressed={result.verdict === "pass"} className={styles.pass} onClick={() => set(result.caseId, { verdict: "pass" })}>
                    <Check aria-hidden /> Lolos
                  </button>
                  <button type="button" aria-pressed={result.verdict === "fail"} className={styles.fail} onClick={() => set(result.caseId, { verdict: "fail" })}>
                    <X aria-hidden /> Tidak
                  </button>
                </div>
              </div>
              <div className={styles.answer} dangerouslySetInnerHTML={{ __html: renderMarkdown(result.answer || "—") }} />
              <div className={styles.expectation}>Patokan: {result.expectation}</div>
              {(result.links?.length || result.expectedLink) && (
                <div className={styles.markers}>
                  Rujukan: {result.links?.length ? result.links.map((link) => link.title).join(", ") : "tidak ada"}
                  {result.expectedLink && (result.linkMatch ? " · sesuai harapan" : ` · yang diharapkan tidak muncul (${result.expectedLink})`)}
                </div>
              )}
              {result.markers.length > 0 && (
                <div className={styles.markers}>Aksi bot: {result.markers.map((m) => (m.type === "lead" ? "tawarkan form kontak" : `alihkan ke WhatsApp (${m.reason})`)).join(", ")}</div>
              )}
              <input
                className={styles.note}
                value={result.note}
                placeholder="Catatan penyesuaian (opsional)"
                onChange={(event) => set(result.caseId, { note: event.target.value })}
                aria-label={`Catatan pertanyaan ${i + 1}`}
              />
              {result.note && <div className={styles.notePrint}>Catatan: {result.note}</div>}
            </li>
          ))}
        </ol>

        <footer className={styles.signatures}>
          <div>
            <p>Klien</p>
            <div className={styles.signLine} />
            <p className={styles.signName}>{data.project.clientName}</p>
          </div>
          <div>
            <p>Navibiz (AI Specialist)</p>
            <div className={styles.signLine} />
            <p className={styles.signName}>{data.createdBy ?? "PT Cetta Adika Nava"}</p>
          </div>
        </footer>
      </article>
    </div>
  );
}
